import EnabledCheckIcon from '@/components/Icons/EnabledCheckIcon';
import UploadIcon from '@/components/Icons/UploadIcon';
import { Cancel, Close } from '@mui/icons-material';
import { Box, IconButton } from '@mui/material';
import classNames from 'classnames';
import { memo, useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';

const MAX_ZIP_FILE_SIZE = 512 * 1024 * 1024;
const MAX_TOTAL_SIZE = 2 * 1024 * 1024 * 1024;
const MAX_FILE_COUNT = 200;

interface IZipFileUploadProps {
  loading: boolean;
  disabled?: boolean;
  setUploadedFiles: (files: File[]) => void;
}

const baseDndClassname = 'flex justify-center items-center flex-col w-full h-[214px] border rounded-lg';

const ZipFileUpload = memo(({ loading, setUploadedFiles, disabled }: IZipFileUploadProps) => {
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [hasUploadFailed, setHasUploadFailed] = useState(false);

  const classNameArgs = {
    'cursor-pointer': !disabled,
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setHasUploadFailed(false);

      if (!acceptedFiles.length) {
        toast.info('Please select one or more ZIP files');
        return;
      }

      setAttachedFiles(prev => {
        const existingNames = new Set(prev.map(f => f.name));
        const newFiles = acceptedFiles.filter(f => !existingNames.has(f.name));
        const merged = [...prev, ...newFiles];

        if (merged.length > MAX_FILE_COUNT) {
          toast.error(`Maximum ${MAX_FILE_COUNT} files allowed`);
          setHasUploadFailed(true);
          return prev;
        }

        const oversizedFile = newFiles.find(f => f.size > MAX_ZIP_FILE_SIZE);
        if (oversizedFile) {
          toast.error(`"${oversizedFile.name}" exceeds the 512 MB per-file limit`);
          setHasUploadFailed(true);
          return prev;
        }

        const totalSize = merged.reduce((sum, f) => sum + f.size, 0);
        if (totalSize > MAX_TOTAL_SIZE) {
          toast.error('Combined file size exceeds the 2 GB limit');
          setHasUploadFailed(true);
          return prev;
        }

        setUploadedFiles(merged);
        return merged;
      });
    },
    [setUploadedFiles],
  );

  const removeFile = useCallback(
    (fileName: string) => {
      setAttachedFiles(prev => {
        const updated = prev.filter(f => f.name !== fileName);
        setUploadedFiles(updated);
        return updated;
      });
    },
    [setUploadedFiles],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: { 'application/zip': ['.zip'] },
    multiple: true,
  });

  if (hasUploadFailed) {
    return (
      <Box
        {...getRootProps()}
        className={classNames(baseDndClassname, 'bg-[#FF3B30] bg-opacity-[.2] border-[#ADACB0]', classNameArgs)}
      >
        <Cancel color='error' sx={{ width: 40, height: 40 }} />
        <span className='text-center text-lg font-semibold justify-between mt-2'>File Upload Failed</span>
        <span className='text-center text-sm font-semibold justify-between mt-3 cursor-pointer'>
          Choose file to retry upload
        </span>
        <input {...getInputProps()} />
      </Box>
    );
  }

  if (attachedFiles.length) {
    return (
      <Box
        {...getRootProps()}
        className={classNames(
          baseDndClassname,
          'bg-[#2C6E49] bg-opacity-[.15] pr-3 border-[#1b4332] !h-auto min-h-[214px] py-3',
          classNameArgs,
        )}
      >
        <input {...getInputProps()} />
        <EnabledCheckIcon width={40} height={40} />
        <span className='text-center text-lg font-semibold justify-between mt-2'>
          {attachedFiles.length === 1 ? 'File Attached' : `${attachedFiles.length} Files Attached`}
        </span>
        <div className='flex flex-col gap-y-1 mt-2 w-full px-3 max-h-[100px] overflow-y-auto'>
          {attachedFiles.map(file => (
            <div key={file.name} className='flex items-center justify-center gap-x-1'>
              <span className='text-[#1b4332] text-sm font-medium truncate'>{file.name}</span>
              {!loading && (
                <IconButton
                  size='small'
                  onClick={e => {
                    e.stopPropagation();
                    removeFile(file.name);
                  }}
                  sx={{ p: 0.25, border: '1px solid #000', borderRadius: '50%' }}
                >
                  <Close sx={{ fontSize: 12, color: '#000' }} />
                </IconButton>
              )}
            </div>
          ))}
        </div>
        {loading ? (
          <span className='text-sm font-semibold mt-3'>Uploading...</span>
        ) : (
          <span className='text-center text-sm font-semibold justify-between mt-3 cursor-pointer hover:text-[#1b4332]'>
            Add more files
          </span>
        )}
      </Box>
    );
  }

  return (
    <Box
      {...getRootProps()}
      className={classNames(baseDndClassname, 'bg-[#F8F8F8] pr-3 border-dashed border-[#ADACB0]', classNameArgs)}
    >
      <input disabled={disabled} {...getInputProps()} />
      <div className='bg-[#E6E6E7] rounded-lg p-2.5'>
        <UploadIcon />
      </div>
      <span className='mt-2 text-sm font-medium'>Drag & drop files or browse files</span>
      <div className='flex mt-1.5 flex-col justify-center items-center'>
        <span className='text-[#8E8F94] text-sm font-normal text-center'>Zip Files Only - Maximum 200 Files</span>
        <span className='text-[#8E8F94] text-sm font-normal text-center'>Maximum 512 MB per file - 2 GB total</span>
      </div>
    </Box>
  );
});

export default ZipFileUpload;
